import React from 'react';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { getFileExtension, getFileNameFromURL } from 'utils/utils';
import { Note } from 'types/types';
import useValidator from '../../../hooks/useValidator';
import uploadAWSS3File from '../../../utils/uploadAWSS3File';
import Modal from '../Modal';
import { IconSprite } from '../../Icon/Icon';
import TextBoxInput from '../../Inputs/TextBoxInput/TextBoxInput';
import Button from '../../Button/Button';

type ModalProps = {
  onCloseModal: () => void;
  onAddNote: (note: Partial<Note>) => void;
  isAddingNote: boolean;
  note?: Note;
  isPrivateNote?: boolean;
  isOwner?: boolean;
};

function NotesForm({
  onCloseModal,
  onAddNote,
  isAddingNote,
  isPrivateNote,
  isOwner,
  note: viewNote,
}: ModalProps) {
  const [validator, showValidationMessage] = useValidator({});
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const [attachment, setAttachment] = React.useState('');
  const [note, setNote] = React.useState<Partial<Note>>({
    text: viewNote?.text || '',
    date: viewNote?.date || moment().format(),
    attachment: viewNote?.attachment || '',
    attachmentBlob: '',
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;

    setNote(fndNote => ({
      ...fndNote,
      [name]: value,
    }));
  };

  const handleAddNote = async () => {
    if (!validator.allValid()) {
      showValidationMessage(true);
      return;
    }

    const payload = {
      ...note,
      text: note.text,
      isPrivate: Boolean(isPrivateNote),
      // ...(viewNote?.id && { id: viewNote.id }),
    };

    onAddNote(payload);
  };

  const handleAttachmentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const inputFile = files[0];

      setUploadingFile(true);

      const filename = inputFile.name;

      const fileType = inputFile.type;

      const fileKey = `document/notes_${uuidv4()}.${getFileExtension(filename)}`;

      const uploadedFile = await uploadAWSS3File(inputFile, fileKey, fileType, 'public');

      setUploadingFile(false);

      setAttachment(filename);

      setNote(fndNote => ({
        ...fndNote,
        attachment: uploadedFile,
        attachmentBlob: URL.createObjectURL(inputFile),
      }));
    }
  };

  return (
    <Modal onClose={onCloseModal} show>
      <div className="rounded-lg bg-white tablet:w-[500px] space-y-4 pt-8 pb-8 px-8">
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold inline-flex whitespace-pre mb-2">
            {viewNote ? (
              <>
                Note
                <span className="ml-auto font-normal text-sm">
                  {viewNote.date
                    ? moment(viewNote.date, 'YYYY-MM-DDTHH:mm:ssZ').format('DD MMMM YYYY')
                    : ''}
                </span>
              </>
            ) : !isPrivateNote ? (
              'Add new note'
            ) : (
              'Add new private note'
            )}
          </h3>

          <div className="fs-exclude space-y-3">
            <TextBoxInput
              name="text"
              placeholder="Add note here..."
              disabled={Boolean(viewNote)}
              value={note.text}
              onChange={handleInputChange}
              rows={8}
              className="bg-[#FAF9F9] border-[1px] border-[#C5CCD0] focus:bg-white 
              peer-[.validator-error]/textToProvider:!border-river-red peer-[.validator-error]/textToProvider:!mb-5 
              peer-[.validator-error]/textToProvider:focus:!ring-river-red"
            />
          </div>
        </div>

        <div className="!mt-2">
          <button
            type="button"
            disabled={Boolean(viewNote)}
            className="relative flex-none border border-gray-300 
            w-full h-[46px] py-2 px-3 rounded-md shadow-none hover:ring-[0.5px]  text-river-black
            flex items-center justify-start cursor-pointer hover:border-river-blue hover:ring-river-blue overflow-hidden 
            disabled:hover:border-gray-300 disabled:hover:ring-gray-300 disabled:hover:ring-transparent disabled:!cursor-not-allowed"
          >
            <IconSprite
              id={
                !(viewNote && (viewNote.attachment || viewNote?.attachmentBlob))
                  ? 'icon-paperclip'
                  : 'icon-fileview'
              }
              className={`${
                !(viewNote && (viewNote.attachment || viewNote?.attachmentBlob))
                  ? 'w-[20px] h-[20px]'
                  : 'w-[24px] h-[24px]'
              } text-[#C2C2C2] cursor-pointer shrink-0`}
            />

            {uploadingFile ? (
              <span className="text-gray-300 font-light whitespace-pre text-[15px] cursor-pointer ml-1">
                Please wait...
              </span>
            ) : (
              <span
                className={`font-light text-[15px] whitespace-pre cursor-pointer overflow-hidden text-ellipsis ml-1 ${
                  attachment || (viewNote && (viewNote.attachment || viewNote?.attachmentBlob))
                    ? 'text-river-black'
                    : 'text-gray-300'
                }`}
              >
                {attachment ||
                  (!(viewNote && (viewNote.attachment || viewNote?.attachmentBlob))
                    ? ''
                    : getFileNameFromURL(
                        note?.attachmentBlob || note?.attachment || 'Click to view file',
                      )) ||
                  'Click here to include file to notes'}
              </span>
            )}
            <input
              disabled={Boolean(viewNote)}
              type="file"
              accept="image/*,.doc,.docx,.pdf"
              className="absolute inset-0 w-full bg-slate-400 h-[46px] opacity-0 file:cursor-pointer hover:cursor-pointer 
              disabled:!border-[#dfe5eb] disabled:!bg-[#F9F9F9] disabled:hover:cursor-not-allowed disabled:!cursor-not-allowed"
              onChange={handleAttachmentUpload}
            />
          </button>
          {viewNote && (viewNote.attachment || viewNote?.attachmentBlob) && (
            <a
              className="text-river-blue text-[15px] mt-1 block underline underline-offset-2"
              target="_blank"
              href={note.attachment || note.attachmentBlob}
              rel="noreferrer"
            >
              Open uploaded file &rarr;
            </a>
          )}
        </div>

        <div
          className="!mt-8 flex flex-row items-center justify-center 
            laptop:justify-end w-full flex-wrap-reverse"
        >
          <Button
            label="Add note"
            onClick={handleAddNote}
            className="w-[200px] !rounded-full !py-[10px] disabled:!bg-[#D2DCE2] disabled:!text-white"
            disabled={uploadingFile || isAddingNote || Boolean(viewNote)}
            loading={isAddingNote}
          />
        </div>
      </div>
    </Modal>
  );
}

export default NotesForm;
